import { faEye, faPen, faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { OIExemptIncomeDetailsForm } from ".";
import { IExemptIncomes, IFormITR1, IReviewOSIncomes } from "../../../config/interfaces";
import { useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector } from "../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const OIExemptIncomeDetails: React.FC = () => {
  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<IExemptIncomes | null>(null);
  const [deleteMsg, setDeleteMsg] = useState<string>("");

  const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();

  const ExemptIncomesDetails = values?.IncomeDetails?.ExemptIncomes;

  const hasNonEmptyValues = (incomeData: IExemptIncomes) => {
    const { InterestPPFIncomes, InterestNREIncomes, OtherExemptIncomes } = incomeData;

    const valuesToCheck = [
      ...(InterestPPFIncomes[0] || []),
      ...(InterestNREIncomes[0] || []),
      ...(OtherExemptIncomes[0] || [])
    ];

    return valuesToCheck.some((value) => value !== "");
  };

  const handleTotalCalculation = (incomeData: IExemptIncomes) => {
    let TotalExemptIncome = 0;
    const ExemptIncomes = Object.values(incomeData || {});
    ExemptIncomes.forEach((incomes) => {
      if (incomes.length) {
        incomes.forEach((item: any) => {
          if (item.length && item[1]) {
            TotalExemptIncome += Number(item[1]);
          }
        });
      }
    });

    const OtherSourcesIncomes: IReviewOSIncomes = {
      ...values.OthersInc,
      TotalExemptIncome: TotalExemptIncome
    };
    const TotalIncomeOthSrc = Object.values(OtherSourcesIncomes)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const DATA = {
      OthersInc: OtherSourcesIncomes,
      IncomeOthSrc: TotalIncomeOthSrc
    };

    setValues({ ...cloneDeep(values), ...DATA });
    if (hasNonEmptyValues(incomeData)) {
      setFieldValue("IncomeDetails.ExemptIncomes", incomeData);
    } else {
      setFieldValue("IncomeDetails.ExemptIncomes", undefined);
    }
  };

  return (
    <>
      <section className="mb-5">
        <div className="section-title-wrap">
          <h5>
            Exempt Income
            <small>Any income from interests from PPF or any other income which is exempt.</small>
          </h5>
          <PreventEditComponent>
            {ExemptIncomesDetails ? (
              <Button variant="warning" size="sm" onClick={() => setShowAddFormCanvas(() => true)}>
                <FASvgIcon icon={faPen} />
                Edit
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddFormCanvas(() => true)}
              >
                <FASvgIcon icon={faPlus} />
                Add
              </Button>
            )}
          </PreventEditComponent>

          {isFilingReadOnly && (
            <Button
              variant="warning"
              size="sm"
              className="text-white"
              onClick={() => setShowAddFormCanvas(() => true)}
            >
              <FASvgIcon icon={faEye} />
              View
            </Button>
          )}
        </div>

        {ExemptIncomesDetails ? (
          <dl className="eds-list eds-dl eds-inline-between">
            <RenderListItem
              title="Interest Income from PPF"
              incomes={ExemptIncomesDetails?.InterestPPFIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Interest Income from PPF");
                setShowDeleteModal({
                  ...ExemptIncomesDetails,
                  InterestPPFIncomes: [["", ""]]
                });
              }}
            />

            <RenderListItem
              title="Interest Income from NRE Accounts"
              incomes={ExemptIncomesDetails?.InterestNREIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Interest Income from NRE Accounts");
                setShowDeleteModal({
                  ...ExemptIncomesDetails,
                  InterestNREIncomes: [["", ""]]
                });
              }}
            />

            <RenderListItem
              title="Any Other Exempt Income"
              incomes={ExemptIncomesDetails?.OtherExemptIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Any Other Exempt Income");
                setShowDeleteModal({
                  ...ExemptIncomesDetails,
                  OtherExemptIncomes: [["", "", ""]]
                });
              }}
            />
          </dl>
        ) : null}
      </section>

      <OIExemptIncomeDetailsForm
        showCanvas={showAddFormCanvas}
        handleCloseBtnPress={(incomeData) => {
          if (incomeData) {
            handleTotalCalculation(incomeData);
          }
          setShowAddFormCanvas(() => false);
        }}
        formValues={ExemptIncomesDetails}
      />

      <ConfirmationModal
        showModal={!!showDeleteModal}
        iconColorClass="text-danger"
        title="Are you sure?"
        message={<p>{`You want to delete ${deleteMsg}.`}</p>}
        onYesBtnClick={() => {
          if (showDeleteModal) {
            handleTotalCalculation(showDeleteModal);
            setShowDeleteModal(() => null);
          }
        }}
        onNoBtnClick={() => setShowDeleteModal(() => null)}
        icon={faTrashAlt}
      />
    </>
  );
};

export default OIExemptIncomeDetails;

type RenderItemProps = {
  incomes: string[][];
  title: string;
  onDeleteBtnClick: () => void;
};

const RenderListItem: React.FC<RenderItemProps> = (props: RenderItemProps) => {
  const { incomes, title, onDeleteBtnClick } = props;
  let sum = 0;
  let hasValue = false;

  if (incomes?.length) {
    incomes.forEach((item) => {
      if (item[0] || item[1] || item[2]) {
        hasValue = true;
      }

      if (item.length && item[1]) {
        sum += parseInt(item[1]);
      }
    });
  }

  return (
    <>
      {hasValue ? (
        <div className="eds-list--item">
          <dt>{title}</dt>
          <dd>
            <span>
              <PriceDisplay price={sum} />
            </span>
            <PreventEditComponent>
              <Button
                variant="transparent"
                className="icon-only"
                onClick={() => onDeleteBtnClick()}
              >
                <FASvgIcon icon={faTrash} />
              </Button>
            </PreventEditComponent>
          </dd>
        </div>
      ) : null}
    </>
  );
};
